"use client";

import { useState } from "react";
import { colOf, columnLabel, rowOf } from "@engine/grid";
import { labelPlacement } from "@engine/roomLabel";
import type { ThemeDefinition } from "@/themes/types";
import type { Board, CellIdx, Character, ObjectKind } from "@engine/types";
import { FurnitureIcon } from "./FurnitureIcon";

const CELL = 44;
const ROOM_LABEL_SIZE = 8;
/** Rough width of one uppercase character, including letter spacing, as a share of font size. */
const ROOM_LABEL_EM = 0.82;
const WALL = 3;
/** Beyond this many marks in one square the rest are summarised, or the square becomes unreadable. */
const MAX_VISIBLE_MARKS = 6;

export interface GridProps {
  board: Board;
  characters: Character[];
  placements: ReadonlyArray<CellIdx | null>;
  notes: Record<string, number[]>;
  exclusions: Record<string, number[]>;
  blocked: ReadonlySet<CellIdx>;
  /** Cells to draw attention to — a violation, or the subject of a hint. */
  highlighted: ReadonlySet<CellIdx>;
  selected: number;
  roomNames: Record<string, string>;
  /** What the piece on a square is called, in this theme and language. */
  objectLabel: (kind: ObjectKind) => string;
  theme: ThemeDefinition;
  onCellTap: (cell: CellIdx) => void;
}

/** How wide a label needs to be to hold its text, roughly. Good enough for a centred pill. */
const labelWidth = (text: string): number => text.length * 5.6 + 12;

export function Grid({
  board,
  characters,
  placements,
  notes,
  exclusions,
  blocked,
  highlighted,
  selected,
  roomNames,
  objectLabel,
  theme,
  onCellTap,
}: GridProps) {
  const colours = theme.colours;
  const size = board.size;

  /**
   * Which piece of furniture is being asked about.
   *
   * Some of these shapes are not obvious, and a `<title>` tooltip is a mouse-only answer. Touching
   * a square names what is on it; so does hovering. It is a label, not a mode — tapping still
   * places, so nothing is spent on finding out what something is.
   */
  const [named, setNamed] = useState<CellIdx | null>(null);

  const namedObject = (() => {
    if (named === null) return null;
    const index = board.objectAt[named] as number;
    const object = index >= 0 ? board.objects[index] : undefined;
    if (!object) return null;

    const row = rowOf(named, size);
    const col = colOf(named, size);
    return {
      row,
      col,
      name: objectLabel(object.kind as ObjectKind),
      cx: col * CELL + CELL / 2,
      // Above the square, except on the top row where there is no room — then just below.
      top: row === 0 ? row * CELL + CELL - 12 : row * CELL - 5,
    };
  })();

  const occupantOf = (cell: CellIdx): number | null => {
    const index = placements.findIndex((c) => c === cell);
    return index === -1 ? null : index;
  };

  const wallSegments: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
  for (let cell = 0; cell < size * size; cell++) {
    const row = rowOf(cell, size);
    const col = colOf(cell, size);
    const room = board.regions[cell];
    const x = col * CELL;
    const y = row * CELL;
    // A wall wherever the room changes, plus the outer border.
    if (row === 0 || board.regions[cell - size] !== room) {
      wallSegments.push({ x1: x, y1: y, x2: x + CELL, y2: y });
    }
    if (row === size - 1 || board.regions[cell + size] !== room) {
      wallSegments.push({ x1: x, y1: y + CELL, x2: x + CELL, y2: y + CELL });
    }
    if (col === 0 || board.regions[cell - 1] !== room) {
      wallSegments.push({ x1: x, y1: y, x2: x, y2: y + CELL });
    }
    if (col === size - 1 || board.regions[cell + 1] !== room) {
      wallSegments.push({ x1: x + CELL, y1: y, x2: x + CELL, y2: y + CELL });
    }
  }

  return (
    <svg
      viewBox={`-18 -18 ${size * CELL + 36} ${size * CELL + 36}`}
      role="grid"
      aria-label="Crime scene"
      data-testid="board-grid"
      // Sizing lives in globals.css: an inline width beats every stylesheet rule, which is what
      // stopped the board from growing into the height a wide window has.
      className="board-svg"
      style={{ touchAction: "manipulation", userSelect: "none" }}
    >
      {/* The edges, always visible: rows numbered, columns lettered, as on a chessboard. */}
      {Array.from({ length: size }, (_, i) => (
        <g key={`labels-${i}`} fill="var(--muted)" fontSize="11" fontFamily="ui-sans-serif, system-ui">
          <text x={-8} y={i * CELL + CELL / 2 + 4} textAnchor="middle">
            {i + 1}
          </text>
          <text x={i * CELL + CELL / 2} y={-6} textAnchor="middle">
            {columnLabel(i)}
          </text>
        </g>
      ))}

      {Array.from({ length: size * size }, (_, cell) => {
        const row = rowOf(cell, size);
        const col = colOf(cell, size);
        const roomId = board.regions[cell] as number;
        const objectIndex = board.objectAt[cell] as number;
        const object = objectIndex >= 0 ? board.objects[objectIndex] : undefined;
        const occupant = occupantOf(cell);
        const key = String(cell);
        const maybes = notes[key] ?? [];
        const noes = exclusions[key] ?? [];
        const standable = board.occupiable[cell];
        const isDimmed = blocked.has(cell) && occupant === null;

        const marks = [
          ...maybes.map((char) => ({ char, kind: "maybe" as const })),
          ...noes.map((char) => ({ char, kind: "no" as const })),
        ].sort((a, b) => a.char - b.char);
        const visible = marks.slice(0, MAX_VISIBLE_MARKS);
        const overflow = marks.length - visible.length;

        return (
          <g
            key={cell}
            data-testid={`cell-${row}-${col}`}
            data-cell={cell}
            onClick={() => standable && onCellTap(cell)}
            onPointerEnter={() => setNamed(object ? cell : null)}
            onPointerLeave={() => setNamed((current) => (current === cell ? null : current))}
            style={{ cursor: standable ? "pointer" : "not-allowed" }}
          >
            <rect
              x={col * CELL}
              y={row * CELL}
              width={CELL}
              height={CELL}
              fill={colours.rooms[roomId % colours.rooms.length]}
            />
            {highlighted.has(cell) && (
              <rect x={col * CELL} y={row * CELL} width={CELL} height={CELL} fill="var(--accent)" opacity={0.28} />
            )}
            {isDimmed && (
              <rect x={col * CELL} y={row * CELL} width={CELL} height={CELL} fill="#000" opacity={0.1} />
            )}

            {object && (
              <FurnitureIcon
                kind={object.kind as ObjectKind}
                label={objectLabel(object.kind as ObjectKind)}
                x={col * CELL}
                y={row * CELL}
                size={CELL}
                occupiable={object.occupiable}
                theme={theme}
              />
            )}

            {/* Pencil marks and exclusions, for every suspect at once. An exclusion is the same
                letter struck through, so a square reads as "could be B, definitely not D". */}
            {occupant === null && marks.length > 0 && (
              <g data-testid={`marks-${row}-${col}`} fontSize="9" fontFamily="ui-sans-serif, system-ui">
                {visible.map((mark, index) => {
                  const mx = col * CELL + 8 + (index % 3) * 13;
                  const my = row * CELL + 15 + Math.floor(index / 3) * 13;
                  const isSelected = mark.char === selected;
                  return (
                    <g key={`${mark.kind}-${mark.char}`}>
                      <text
                        x={mx}
                        y={my}
                        textAnchor="middle"
                        fill={mark.kind === "no" ? colours.markNo : colours.markMaybe}
                        opacity={mark.kind === "no" ? 0.85 : 1}
                        fontWeight={isSelected ? 700 : 400}
                      >
                        {characters[mark.char]?.letter}
                      </text>
                      {mark.kind === "no" && (
                        <line
                          x1={mx - 5}
                          y1={my - 3}
                          x2={mx + 5}
                          y2={my - 3}
                          stroke={colours.markNo}
                          strokeWidth={1.4}
                        />
                      )}
                    </g>
                  );
                })}
                {overflow > 0 && (
                  <text
                    x={col * CELL + CELL - 7}
                    y={row * CELL + CELL - 5}
                    textAnchor="middle"
                    fill="var(--muted)"
                    fontSize="8"
                  >
                    +{overflow}
                  </text>
                )}
              </g>
            )}

            {occupant !== null && (
              <g data-testid={`placed-${row}-${col}`}>
                <circle
                  cx={col * CELL + CELL / 2}
                  cy={row * CELL + CELL / 2}
                  r={CELL * 0.33}
                  fill={characters[occupant]?.isVictim ? colours.victimToken : colours.suspectToken}
                />
                <text
                  x={col * CELL + CELL / 2}
                  y={row * CELL + CELL / 2 + 5}
                  textAnchor="middle"
                  fontSize="15"
                  fontWeight="700"
                  fill="#fff"
                >
                  {characters[occupant]?.letter}
                </text>
              </g>
            )}
          </g>
        );
      })}

      <g stroke={colours.wall} strokeWidth={WALL} strokeLinecap="square">
        {wallSegments.map((segment, index) => (
          <line key={index} x1={segment.x1} y1={segment.y1} x2={segment.x2} y2={segment.y2} />
        ))}
      </g>

      {/* Room names, once per room, on the clearest run of squares that room has. */}
      {board.rooms.map((room) => {
        const name = roomNames[String(room.id)] ?? `Room ${room.id + 1}`;
        // Deliberately generous: we cannot measure text before drawing it, and over-estimating
        // only squeezes a name that would have just fitted. Under-estimating lets it spill into
        // the room next door, which is the thing being fixed.
        const width = ROOM_LABEL_SIZE * ROOM_LABEL_EM * name.length;
        const spot = labelPlacement(board, room, Math.ceil(width / CELL));
        const available = spot.span * CELL - 6;

        const squeezed = Math.min(width, available);

        return (
          <g key={room.id}>
            {/* Furnished wall to wall: no clear spot exists, so make the name readable where it is. */}
            {!spot.clear && (
              <rect
                data-testid={`room-plate-${room.id}`}
                x={spot.centreColumn * CELL - squeezed / 2 - 3}
                y={spot.row * CELL + CELL / 2 - 8}
                width={squeezed + 6}
                height={13}
                rx={3}
                fill={colours.plate}
                opacity={0.82}
              />
            )}
          <text
            data-testid={`room-name-${room.id}`}
            x={spot.centreColumn * CELL}
            y={spot.row * CELL + CELL / 2 + 3}
            textAnchor="middle"
            fontSize={ROOM_LABEL_SIZE}
            fontWeight="600"
            fill={colours.roomLabel}
            // A long name in a narrow room is squeezed rather than allowed to spill into the room
            // next door, where it would look like it belonged there.
            {...(width > available ? { textLength: available, lengthAdjust: "spacingAndGlyphs" as const } : {})}
            style={{ pointerEvents: "none", textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            {name}
          </text>
          </g>
        );
      })}

      {/* The furniture label, drawn last so nothing can cover it. */}
      {namedObject && (
        <g pointerEvents="none" data-testid={`object-label-${namedObject.row}-${namedObject.col}`}>
          <rect
            x={namedObject.cx - labelWidth(namedObject.name) / 2}
            y={namedObject.top}
            width={labelWidth(namedObject.name)}
            height={16}
            rx={8}
            fill="#2c2823"
            opacity={0.94}
          />
          <text
            x={namedObject.cx}
            y={namedObject.top + 11.5}
            textAnchor="middle"
            fontSize="9.5"
            fontFamily="ui-sans-serif, system-ui"
            fill="#fff"
          >
            {namedObject.name}
          </text>
        </g>
      )}
    </svg>
  );
}
